import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { safeGetItem, safeSetItem, safeRemoveItem } from '@/lib/browserStorage';

// Static JSON imports so server-side rendering has access to translations and
// avoids hydration mismatches. These are small files and bundling them for
// SSR is acceptable.
import esTranslation from '../../public/locales/es/translation.json';
import enTranslation from '../../public/locales/en/translation.json';
import bgTranslation from '../../public/locales/bg/translation.json';
import roTranslation from '../../public/locales/ro/translation.json';

// Language storage key (localStorage only for language preference)
export const LANG_KEY = 'wsc:lang:v1';
// Shared sync keys used across the app to notify other tabs/components
export const SETTINGS_SYNC_KEY = 'wsc:settings:updated';
export const LANG_SYNC_KEY = 'wsc:lang:updated';
// Persistent settings storage key moved to localforage, kept for backward compat
export const SETTINGS_KEY = 'wsc:settings:v1';
// In-window broadcaster so hook instances within the same tab update immediately.
export const LANG_EVENT_NAME = 'wsc:lang:changed';
const _LANG_EVENT_TARGET: EventTarget | null =
  typeof window !== 'undefined' ? new EventTarget() : null;
export const LANG_EVENT_TARGET = _LANG_EVENT_TARGET;

export type LangCode = 'es' | 'en' | 'bg' | 'ro';
export type Lang = Record<string, string>;

// Allowed language codes used by the app
const ALLOWED_LANGS = new Set<LangCode>(['es', 'en', 'bg', 'ro']);

// initialize i18next. On the server we synchronously preload JSON files from
// the `public/locales` folder so t() works during SSR and avoids hydration
// mismatches. On the client, keep the http backend for lazy loading.
if (!i18n.isInitialized) {
  // Use statically imported JSON resources for both server and client so
  // translations are available synchronously at first render. This avoids
  // hydration mismatches caused by the client lazy-loading translations.
  const resources = {
    es: { translation: esTranslation },
    en: { translation: enTranslation },
    bg: { translation: bgTranslation },
    ro: { translation: roTranslation },
  };

  i18n.use(initReactI18next).init({
    resources,
    fallbackLng: 'es',
    lng: 'es',
    debug: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
}

// Returns the current validated language code (or 'es' if unknown).
export function getCurrentLang(): LangCode {
  const current = safeGetItem(LANG_KEY) as LangCode | null;
  if (current && ALLOWED_LANGS.has(current)) {
    return current;
  }
  const fromI18n = i18n.language as LangCode | undefined;
  return fromI18n && ALLOWED_LANGS.has(fromI18n) ? fromI18n : 'es';
}

export function setLang(code: LangCode) {
  if (typeof window === 'undefined') {
    return;
  }
  // Store language preference in localStorage
  safeSetItem(LANG_KEY, code);

  // Change i18n language
  try {
    i18n.changeLanguage(code);
  } catch (e) {
    // ignore
  }

  // Broadcast language change via localStorage signal for cross-tab sync
  const lstamp = Date.now().toString();
  safeSetItem(LANG_SYNC_KEY, lstamp);
  window.setTimeout(() => {
    try {
      if (safeGetItem(LANG_SYNC_KEY) === lstamp) {
        safeRemoveItem(LANG_SYNC_KEY);
      }
    } catch (e) {
      // ignore
    }
  }, 1000);

  // Also broadcast settings sync signal so settings listeners pick up language change
  const stamp = Date.now().toString();
  safeSetItem(SETTINGS_SYNC_KEY, stamp);
  window.setTimeout(() => {
    try {
      if (safeGetItem(SETTINGS_SYNC_KEY) === stamp) {
        safeRemoveItem(SETTINGS_SYNC_KEY);
      }
    } catch (e) {
      // ignore
    }
  }, 1000);

  // Dispatch in-window event for same-tab subscribers
  try {
    if (LANG_EVENT_TARGET) {
      LANG_EVENT_TARGET.dispatchEvent(new CustomEvent(LANG_EVENT_NAME, { detail: code }));
    }
  } catch (e) {
    // ignore
  }
}

/**
 * Backwards-compatible hook that returns the same shape as before:
 * an object with all translation keys (flattened), plus __code and setLang.
 * Internally it uses react-i18next.
 */
// Compatibility hook intentionally removed. Please use `useTranslation()` and
// the `t()` helper directly in components. The `setLang()` helper remains
// exported to keep the language persistence and cross-tab sync behavior.

export default i18n;
